import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { fmtAestDate, todayAestISO } from "../lib/format";
import CompleteTaskModal from "../components/CompleteTaskModal";
import TaskEditModal from "../components/TaskEditModal";
import { taskTypePillClass } from "../lib/taskTypes";

export default function Tasks() {
  const { user } = useAuth();
  const [sp, setSp] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [rep, setRep] = useState("");
  const [status, setStatus] = useState("open");
  const [due, setDue] = useState(sp.get("due") || "all");
  const [users, setUsers] = useState([]);
  const [completing, setCompleting] = useState(null);
  const [editing, setEditing] = useState(null);

  const reload = useCallback(async () => {
    try {
      const p = new URLSearchParams();
      if (rep) p.set("rep_id", rep);
      if (status) p.set("status", status);
      const { data } = await api.get(`/tasks?${p}`);
      setRows(data);
    } catch {
      // Network/auth failure - leave list empty; user can retry via filter change.
      setRows([]);
    }
  }, [rep, status]);
  useEffect(() => { reload(); }, [reload]);
  useEffect(() => { api.get("/users").then(r => setUsers(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    const next = new URLSearchParams(sp);
    if (due !== "all") next.set("due", due); else next.delete("due");
    setSp(next, { replace: true });
  }, [due, sp, setSp]);

  const today = todayAestISO();
  const filtered = useMemo(() => {
    return rows.filter(t => {
      if (due === "today") return t.due_date === today;
      if (due === "overdue") return t.status === "open" && t.due_date < today;
      if (due === "upcoming") return t.due_date > today;
      return true;
    });
  }, [rows, due, today]);

  async function toggle(t) {
    if (t.status === "open") { setCompleting(t); return; }
    await api.patch(`/tasks/${t.id}`, { status: "open" });
    reload();
  }
  return (
    <div className="space-y-4" data-testid="tasks-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold">Tasks</h1>
          <p className="text-sm text-gray-500">
            {filtered.length} task{filtered.length!==1?"s":""}
            {due !== "all" && <span className="ml-2 pill pill-blue uppercase">{due}</span>}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <select className="cesco-input" value={due} onChange={e=>setDue(e.target.value)}
                  data-testid="tasks-filter-due">
            <option value="all">All dates</option>
            <option value="today">Due today</option>
            <option value="overdue">Overdue</option>
            <option value="upcoming">Upcoming</option>
          </select>
          <select className="cesco-input" value={status} onChange={e=>setStatus(e.target.value)}
                  data-testid="tasks-filter-status">
            <option value="open">Open</option><option value="completed">Completed</option><option value="all">All</option>
          </select>
          {user?.role === "admin" && (
            <select className="cesco-input" value={rep} onChange={e=>setRep(e.target.value)}>
              <option value="">My tasks</option>
              {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          )}
        </div>
      </div>
      <div className="space-y-2">
        {filtered.length === 0 && <div className="cesco-card p-6 text-center text-gray-500">No tasks 🎉</div>}
        {filtered.map(t => {
          const overdue = t.status === "open" && t.due_date < today;
          return (
            <div key={t.id} className={`p-3 rounded-md flex items-start gap-3 ${t.status === "open" ? "feed-item-task-open" : "feed-item-task-done"}`}
                 data-testid="task-row">
              <input type="checkbox" className="mt-1" checked={t.status === "completed"} onChange={()=>toggle(t)}/>
              <div className="flex-1">
                <div className={`font-medium ${t.status === "completed" ? "line-through" : ""}`}>{t.summary}</div>
                <div className="text-xs text-gray-600 mt-1 flex flex-wrap gap-x-2 gap-y-1 items-center">
                  {t.task_type && <span className={`pill ${taskTypePillClass(t.task_type)}`} data-testid="task-type-pill">{t.task_type}</span>}
                  <Link to={`/companies/${t.company_id}`} className="hover:underline">{t.company_name}</Link>
                  {t.contact_name && <span className="pill pill-blue">👤 {t.contact_name}</span>}
                  <span>· Due {fmtAestDate(t.due_date)}{t.due_time ? ` ${t.due_time}` : ""}</span>
                  {overdue && <span className="pill pill-red">OVERDUE</span>}
                </div>
                {t.status === "completed" && t.outcome && (
                  <div className="text-xs text-gray-700 mt-1 italic">Outcome: {t.outcome}</div>
                )}
              </div>
              {t.status === "open" && (
                <div className="flex gap-2 shrink-0">
                  <button className="cesco-btn-ghost text-xs" onClick={()=>setEditing(t)}
                          data-testid="btn-edit-task">Edit</button>
                  <button className="cesco-btn-ghost text-xs" onClick={()=>setCompleting(t)}
                          data-testid="btn-complete-task">Complete</button>
                </div>
              )}
            </div>
          );
        })}
      </div>
      {completing && (
        <CompleteTaskModal task={completing} users={users}
          onClose={()=>setCompleting(null)} onDone={reload}/>
      )}
      {editing && (
        <TaskEditModal task={editing} users={users}
          onClose={()=>setEditing(null)} onSaved={reload}/>
      )}
    </div>
  );
}
